// El carrito del negocio elegido, guardado en localStorage por tienda y negocio en cada cambio. Sin cuenta (decisión
// D49) hay un carrito del visitante, también guardado: al entrar, lo elegido pasa al carrito del negocio.
import { create } from 'zustand';
import { alCambiarEnOtraPestana, borrar, guardar, leer } from './almacen';
import { type Carrito, carritoValido, carritoVacio, type ResumenUnion, unirCarritos } from './carrito';
import { claveLocal } from './tienda-actual';

/** El carrito de quien mira la tienda sin cuenta. */
export const VISITANTE = 'visitante';

interface EstadoCarrito {
  negocioId: string | null;
  carrito: Carrito;
  /** Al entrar se juntó lo elegido sin cuenta con lo que el negocio tenía guardado: el carrito lo cuenta. */
  unido: ResumenUnion | null;
}

export const claveCarrito = (negocioId: string) => claveLocal(`carrito:${negocioId}`);

export const useCarrito = create<EstadoCarrito>(() => ({ negocioId: null, carrito: carritoVacio(), unido: null }));

let dejarDeEscuchar: (() => void) | null = null;

/**
 * Abre el carrito guardado de un negocio (al entrar o al cambiar de negocio) o el del visitante. Si había un carrito
 * del visitante, lo elegido pasa al del negocio: lo del visitante manda en lo repetido y lo demás se suma.
 */
export function abrirCarrito(negocioId: string | null): void {
  if (useCarrito.getState().negocioId === negocioId) return;
  dejarDeEscuchar?.();
  dejarDeEscuchar = null;
  if (!negocioId) {
    useCarrito.setState({ negocioId: null, carrito: carritoVacio(), unido: null });
    return;
  }
  const clave = claveCarrito(negocioId);
  let carrito = carritoValido(leer(clave));
  let unido: ResumenUnion | null = null;
  if (negocioId !== VISITANTE) {
    const visita = carritoValido(leer(claveCarrito(VISITANTE)));
    if (visita.lineas.length) {
      const u = unirCarritos(carrito, visita);
      carrito = u.carrito;
      unido = u.resumen;
      guardar(clave, carrito);
      borrar(claveCarrito(VISITANTE));
    }
  }
  useCarrito.setState({ negocioId, carrito, unido });
  // El mismo negocio abierto en otra pestaña: se ve lo mismo en las dos.
  if (typeof window !== 'undefined')
    dejarDeEscuchar = alCambiarEnOtraPestana(clave, (v) => useCarrito.setState({ carrito: carritoValido(v) }));
}

/** Aplica un cambio y lo guarda. */
export function cambiarCarrito(f: (c: Carrito) => Carrito): void {
  const { negocioId, carrito } = useCarrito.getState();
  const nuevo = f(carrito);
  if (nuevo === carrito) return;
  useCarrito.setState({ carrito: nuevo });
  if (!negocioId) return;
  if (nuevo.lineas.length || nuevo.opciones.observaciones || nuevo.clave) guardar(claveCarrito(negocioId), nuevo);
  else borrar(claveCarrito(negocioId));
}

export const vaciarCarrito = () => cambiarCarrito(() => carritoVacio());

/** El carrito ya mostró que se juntó lo elegido sin cuenta. */
export const olvidarUnion = () => useCarrito.setState({ unido: null });
